import {
  type ControllerFieldState,
  type ControllerProps,
  type ControllerRenderProps,
  type FieldPath,
  type FieldValues,
  get,
  useFormContext,
  type UseFormStateReturn,
} from "react-hook-form";
import { useMemo, useState } from "react";
import { useSelect } from "src/system/useSelect.tsx";
import type { SelectV2Props } from "src/components/SelectV2/SelectV2.tsx";
import type {
  SingleSelectOptions,
  SingleSelectServerOptions,
} from "src/components/Form/Form.types.ts";
import { getResourceFieldOptions } from "src/system/getResourceFieldOptions.ts";
import { useTranslation } from "react-i18next";
import { useServerSelectFilter } from "src/system/useServerSelectFilter.ts";

export type UseSingleSelectFieldProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<SelectV2Props, "onChange" | "options"> &
  Omit<ControllerProps<TFieldValues, TName>, "render"> & {
    name: TName;
    forceValue?: any;
    locale?: string;
    isButton?: boolean;
    onChange?: (value: any, form: any, resource: any) => void;
    options?: SingleSelectOptions;
    serverOptions?: SingleSelectServerOptions;
  };

export const useSingleSelectField = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: UseSingleSelectFieldProps<TFieldValues, TName>,
) => {
  const {
    disabled,
    name,
    locale,
    options: optionsProp = [],
    onChange: onChangeProp,
    onBlur: onBlurProp,
    serverOptions,
    forceValue,
    ...restComponentProps
  } = props;
  const { t } = useTranslation();

  const [isOpen, setIsOpen] = useState(false);

  const form = useFormContext();
  const filter = useServerSelectFilter({
    ...serverOptions,
  });

  const {
    options: resourceOptions,
    query: {
      isLoading: isLoadingResourceOptions,
      refetch: refetchResourceList,
    },
    getResourceByValue,
  } = useSelect({
    locale,
    resource: serverOptions ? serverOptions.resource : "",
    valueProp: serverOptions?.valueProp,
    labelProp: serverOptions?.labelProp,
    include: serverOptions?.include,
    useSimplePagination: true,
    disablePagination: true,
    groupBy: serverOptions?.groupBy,
    groups: serverOptions?.groups,
    processOptions: serverOptions?.processOptions,
    filter: filter,
    sort: serverOptions?.sort,
    queryOptions: {
      enabled: !!serverOptions,
    },
  });

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open && serverOptions) {
      refetchResourceList();
    }
  };

  const options = useMemo(() => {
    if (serverOptions) return resourceOptions;
    if (Array.isArray(optionsProp)) return optionsProp;

    return getResourceFieldOptions({
      resource: optionsProp.resource,
      field: optionsProp.field,
      t,
    });
  }, [serverOptions, optionsProp, resourceOptions, t]);

  const getSelectProps = <
    TFieldValues extends FieldValues = FieldValues,
    TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
  >({
    field: { value, onChange, onBlur },
    formState: { errors },
  }: {
    field: ControllerRenderProps<TFieldValues, TName>;
    fieldState: ControllerFieldState;
    formState: UseFormStateReturn<TFieldValues>;
  }): SelectV2Props => {
    const resolvedValue = forceValue != null ? forceValue : value;

    const isLoadingData = serverOptions ? isLoadingResourceOptions : false;
    return {
      value: resolvedValue,
      name: name,
      onOpenChange: handleOpenChange,
      isLoadingData: isLoadingData,
      disabled: disabled,
      isLoadingSelectedOption: isLoadingData,
      isOpen: isOpen,
      onChange: (value) => {
        const resource = getResourceByValue(value);
        onChangeProp?.(value, form, resource);
        onChange(value);
      },

      options: options,
      onBlur: (event) => {
        onBlurProp?.(event);
        onBlur();
      },
      error: get(errors?.root || {}, name)?.message, // server error
      ...restComponentProps,
    };
  };
  return {
    getSelectProps,
  };
};
